export type DmOfferAction = 'offer' | 'accepted' | 'declined' | 'counter' | 'agreed';

export type ParsedDmOfferMessage = {
  action: DmOfferAction;
  offerId: string;
  body: string;
  listingPath: string | null;
};

export type DmListingOfferPayload = {
  offerId: string;
  listingId: string;
  listingTitle: string;
  listPrice: number;
  offerAmount: number;
  buyerNote?: string | null;
  buyerName?: string;
};

export function parseDmListingOfferMessage(content: string): ParsedDmOfferMessage | null {
  const trimmed = content.trimStart();
  const marker = trimmed.match(/^\[d88-offer(?::(accepted|declined|counter|agreed))?:(\{)?([^\]]+)\]/);
  if (!marker) return null;
  const action = (marker[1] as DmOfferAction | undefined) || 'offer';
  const offerId = marker[3]?.trim();
  if (!offerId) return null;
  const body = trimmed.slice(marker[0].length).trim();
  const listingLine = body.split('\n').find((line) => line.trim().startsWith('/market/product/'));
  return {
    action,
    offerId,
    body,
    listingPath: listingLine?.trim() || null,
  };
}

export function buildDmListingOfferMessage(opts: DmListingOfferPayload): string {
  const title = opts.listingTitle.trim() || 'this listing';
  const who = opts.buyerName?.trim() || 'I';
  const lines = [
    `[d88-offer:${opts.offerId}]`,
    `${who} offered $${opts.offerAmount.toFixed(2)} on "${title}" (listed at $${opts.listPrice.toFixed(2)}).`,
    `/market/product/${opts.listingId}`,
  ];
  const note = opts.buyerNote?.trim();
  if (note) lines.push('', note);
  return lines.join('\n');
}

export function buildDmOfferAcceptedMessage(opts: {
  offerId: string;
  listingId: string;
  listingTitle: string;
  offerAmount: number;
  sellerName?: string;
  deadlineLabel?: string;
  windowLabel?: string;
}): string {
  const who = opts.sellerName?.trim() || 'Seller';
  const lines = [
    `[d88-offer:accepted:${opts.offerId}]`,
    `${who} accepted your $${opts.offerAmount.toFixed(2)} offer on "${opts.listingTitle}".`,
    opts.windowLabel
      ? `Listing on hold for ${opts.windowLabel} — complete checkout before time runs out.`
      : 'Listing on hold — complete checkout before time runs out.',
  ];
  if (opts.deadlineLabel) lines.push(`Deadline: ${opts.deadlineLabel}.`);
  lines.push(`/market/product/${opts.listingId}`);
  return lines.join('\n');
}

export function buildDmOfferDeclinedMessage(opts: {
  offerId: string;
  listingId: string;
  listingTitle: string;
  offerAmount: number;
  sellerName?: string;
}): string {
  const who = opts.sellerName?.trim() || 'Seller';
  return [
    `[d88-offer:declined:${opts.offerId}]`,
    `${who} declined your $${opts.offerAmount.toFixed(2)} offer on "${opts.listingTitle}".`,
    `/market/product/${opts.listingId}`,
  ].join('\n');
}

export function buildDmOfferCounterMessage(opts: {
  offerId: string;
  listingId: string;
  listingTitle: string;
  previousAmount: number;
  counterAmount: number;
  sellerName?: string;
  buyerName?: string;
  by?: 'seller' | 'buyer';
  note?: string | null;
}): string {
  const by = opts.by ?? 'seller';
  const who = by === 'seller'
    ? (opts.sellerName?.trim() || 'Seller')
    : (opts.buyerName?.trim() || 'Buyer');
  const lines = [
    `[d88-offer:counter:${opts.offerId}]`,
    by === 'seller'
      ? `${who} countered at $${opts.counterAmount.toFixed(2)} on "${opts.listingTitle}" (your offer was $${opts.previousAmount.toFixed(2)}).`
      : `${who} countered at $${opts.counterAmount.toFixed(2)} on "${opts.listingTitle}" (your counter was $${opts.previousAmount.toFixed(2)}).`,
    `/market/product/${opts.listingId}`,
  ];
  const note = opts.note?.trim();
  if (note) lines.push('', note);
  return lines.join('\n');
}

export function buildDmBuyerAgreedCounterMessage(opts: {
  offerId: string;
  listingId: string;
  listingTitle: string;
  offerAmount: number;
  buyerName?: string;
}): string {
  const who = opts.buyerName?.trim() || 'Buyer';
  return [
    `[d88-offer:agreed:${opts.offerId}]`,
    `${who} accepted your counter offer of $${opts.offerAmount.toFixed(2)} on "${opts.listingTitle}".`,
    'Choose a purchase window to put the listing on hold for this buyer.',
    `/market/product/${opts.listingId}`,
  ].join('\n');
}

export function buildDmBuyerDeclinedCounterMessage(opts: {
  offerId: string;
  listingId: string;
  listingTitle: string;
  offerAmount: number;
  buyerName?: string;
}): string {
  const who = opts.buyerName?.trim() || 'Buyer';
  return [
    `[d88-offer:declined:${opts.offerId}]`,
    `${who} declined your counter offer of $${opts.offerAmount.toFixed(2)} on "${opts.listingTitle}".`,
    `/market/product/${opts.listingId}`,
  ].join('\n');
}

export function formatDmOfferPreview(text: string): string | null {
  const parsed = parseDmListingOfferMessage(text);
  if (!parsed) return null;
  const firstLine = parsed.body.split('\n').find((line) => line.trim()) || '';
  if (firstLine) return firstLine;
  if (parsed.action === 'accepted') return 'Offer accepted';
  if (parsed.action === 'declined') return 'Offer declined';
  if (parsed.action === 'counter') return 'Counter offer';
  if (parsed.action === 'agreed') return 'Buyer accepted counter';
  return 'Offer';
}
